import {AInesAssistantType, AInesFeatureSet} from "@/graphql/generated/graphql";

import {getAssistentForFeatureSet, getFeatureSetsForAssistant} from "@/helper/chat/assistantFeatureMapping";
import {AssistantRun} from "@/helper/chat/assistantRun/assistantRun";
import {consoleErrorChat, consoleLogChat} from "@/helper/console";

import {AssistantRunConfig} from "@/helper/chat/assistantRun/assistantRunConfig";
import {AssistantRunStatus} from "@/helper/chat/assistantRun/assistantRunStatus";
import {kapitelErrorHandler} from "@/helper/error";
import OpenAI from "openai";
import {
    generateAssistantDeveloperMessage,
    getAssistantFromStore,
    persistMessage,
    runCompletion
} from "@/helper/chat/assistantAPI";
import {makeMessagePersistParams} from "@/helper/chat/chatBL";
import {addMessageToThread, getMessagesFromThreadStore} from "@/helper/chat/threadStore";
import {Stream} from "openai/streaming";
import {generateAdditionalInstructions} from "@/helper/chat/additionalInstructions";
import {useChatStore} from "@/helper/chat/chatStore";
import {listFormat, pluralizeUnitOnly} from "@/helper/amountFormatter";
import {generateMetaPromptMessageThread} from "@/helper/chat/prompt";


export class AssistantRunChooser extends AssistantRun {

    private choicePromiseResolve: any
    public assistantChoicePromise : Promise<AInesAssistantType|undefined>

    public response : string|object|undefined

    constructor(
        public runConfig: AssistantRunConfig,
        public userMessage: string,
        protected isScriptedContent: boolean
    ) {
        super(runConfig, userMessage, isScriptedContent)

        // prepare choice promise
        this.assistantChoicePromise = new Promise((resolve, reject) => {
            this.choicePromiseResolve = resolve;
            // choicePromiseReject = reject;
        });

        addMessageToThread(userMessage, this.runConfig.threadId)
            .then( () => {
                makeMessagePersistParams({
                    config: this.runConfig,
                    role: "user",
                    content: userMessage,
                    isScriptedContent: isScriptedContent
                }).then(persistMessage)
            })
            .then(this.runCompletion)
            .then(this.handleResponse)
            .catch((reason) => {
                // state: on content complete progress state from streaming > failed
                this.setState(AssistantRunStatus.FAILED)
                // streamComplete: event
                this.mitt.emit('runFailed')

                const msg = 'Run ' + this.getRunId() + ' failed: ' /* + this.getLastError()?.code + ' - ' + this.getLastError()?.message */
                const e = new Error(msg, {cause: reason});

                // log to chat console
                consoleErrorChat(msg, this, e, reason);

                // handle error (toast, sentry)
                kapitelErrorHandler(e)
            })

        this.setState(AssistantRunStatus.REQUESTED);
    }

    private handleResponse = async (response: OpenAI.ChatCompletion) => {
        // resolve choice promise
        const content = response.choices[0]?.message.content || ''

        let message
        try {
            message = JSON.parse(content)
        } catch {
            message = undefined
        }
        this.response = message || content || response

        this.mitt.emit('runComplete')
        this.setState(AssistantRunStatus.COMPLETED)

        // meta assistant (chooser) response message
        addMessageToThread(
            content,
            this.runConfig.threadId,
            "assistant"
        ).then(() => {
            makeMessagePersistParams({
                config: this.runConfig,
                role: "assistant",
                content: content,
                runId: this.getRunId()
            }).then(persistMessage)
        })

        const assistant = (message && message.chosenExpertAssistant && AInesAssistantType.hasOwnProperty(message.chosenExpertAssistant as string)) ? (message.chosenExpertAssistant as AInesAssistantType) : undefined

        consoleLogChat(' %s responded: %o %O',AInesAssistantType[this.runConfig.assistantType], assistant, this.response)

        this.choicePromiseResolve(assistant)
    }

    private runCompletion = async () => {

        const promptMessage = await generateAssistantDeveloperMessage(this.runConfig)
        const chatHistoryMessages = await generateMetaPromptMessageThread(this.userMessage)

        const promptInfo = getAssistantFromStore(this.runConfig)

        consoleLogChat(' %s: run completion with thread %O', AInesAssistantType[this.runConfig.assistantType], chatHistoryMessages)

        const completion = runCompletion({
            model: promptInfo.model,
            // tools: (promptInfo.tools && promptInfo.tools.length > 0) ? promptInfo.tools : undefined,
            response_format: promptInfo.response_format,
            messages: [promptMessage as OpenAI.ChatCompletionMessageParam].concat(chatHistoryMessages),
        })

        this.eventTracker.track('requestStart')
        completion
            .then(() => this.eventTracker.track('responseEnd'))
            .catch(() => this.eventTracker.track('cancelled'))

        return completion
    }

}
